import { IconArrowsShuffle, IconBroadcast, IconGift, IconLock, IconMessages, IconUsers } from "@tabler/icons-react"
import { type FC } from "react"

import { cn } from "@forento/ui/utilities/tailwind"

import { useToast } from "~/contexts/ToastContext"
import { useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

const benefits = [
	{ icon: IconBroadcast, label: "Live webinars and training with the Forento team" },
	{ icon: IconArrowsShuffle, label: "Exclusive co-marketing opportunities with Forento" },
	{ icon: IconMessages, label: "Connect with other creators" },
	{ icon: IconGift, label: "Forento AI announcements and credit airdrops" },
]

const members = ["HN", "CP", "TZ", "MI"]

const CreatorCommunityBanner: FC = () => {
	const user = useUser()
	const toast = useToast()
	const dismissBanner = query.platform.dismissCreatorCommunityBanner.useMutation({
		async onSuccess() {
			await user.reload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to dismiss the banner. Please try again.")
		},
	})

	return (
		<section className="overflow-hidden rounded-2xl border border-violet-500/40 bg-[#1d174f] text-white shadow-lg">
			<div className="flex flex-col gap-8 p-6 lg:flex-row lg:items-center lg:justify-between lg:p-7">
				<div className="flex min-w-0 flex-1 flex-col items-start">
					<div className="mb-4 flex items-center gap-3">
						<div className="flex size-9 items-center justify-center rounded-lg bg-violet-500 text-white shadow-md">
							<IconUsers className="size-5" stroke={2.5} />
						</div>
						<span className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium text-violet-100">
							<IconLock className="size-4" />
							Private, invite only
						</span>
					</div>
					<h2 className="max-w-xl text-xl leading-tight font-semibold text-balance sm:text-2xl">
						Join our free Creator Community
					</h2>
					<ul className="mt-4 flex flex-col gap-2 text-sm text-violet-100 sm:text-[15px]">
						{benefits.map(benefit => (
							<li key={benefit.label} className="flex items-start gap-2.5">
								<benefit.icon className="mt-0.5 size-4 shrink-0 text-violet-400" />
								<span>{benefit.label}</span>
							</li>
						))}
					</ul>
					<div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
						<a
							href="https://forento.link/creator-community"
							target="_blank"
							rel="noreferrer noopener"
							onClick={() => dismissBanner.mutate()}
							className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-violet-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-violet-400"
						>
							Join the community
						</a>
						<button
							type="button"
							onClick={() => dismissBanner.mutate()}
							disabled={dismissBanner.isPending}
							className="cursor-pointer text-sm font-medium text-violet-200/70 transition-colors hover:text-white disabled:cursor-auto disabled:opacity-50"
						>
							Maybe later
						</button>
					</div>
				</div>
				<div className="w-full shrink-0 rounded-xl bg-white/5 p-6 lg:w-[min(42%,360px)]">
					<div className="flex items-center">
						{members.map((member, index) => (
							<span
								key={member}
								className={cn(
									"flex size-11 items-center justify-center rounded-full border-2 border-[#1d174f] text-sm font-semibold",
									index % 2 === 0 ? "bg-violet-600" : "bg-violet-400 text-violet-950",
									index > 0 && "-ml-3",
								)}
							>
								{member}
							</span>
						))}
					</div>
					<h3 className="mt-6 text-lg leading-tight font-semibold text-balance sm:text-xl">
						Private members group of creators
					</h3>
					<p className="mt-2 text-sm leading-relaxed text-violet-100/70 sm:text-[15px]">
						Less than 100 members, join early and grow with us
					</p>
				</div>
			</div>
		</section>
	)
}

export default CreatorCommunityBanner
